import { useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { API, Auth } from "aws-amplify";
import {
  getGoogleRoomDetails,
  GetPodSession,
  getUser,
  GetOrganizationDetails,
} from "../graphql/queries";
import { UpdatePodSession, updateUser, createUser } from "../graphql/mutations";
import {
  Box,
  Card,
  Text,
  CardHeader,
  CardBody,
  NameValueList,
  NameValuePair,
  Image,
  Layer,
  Button,
  TextArea,
  TextInput,
} from "grommet";
import { useGoogleLogin } from "@react-oauth/google";
import { getOrganizationAction } from "../config/organizationActions";
import MonitorEDUQuickButtons from "../Components/MonitorEDUQuickButtons";

export default function PodSession() {
  const [loading, setLoading] = useState(true);
  const [podSession, setPodSession] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [user, setUser] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [meetLoading, setMeetLoading] = useState(false);
  const [localNotes, setLocalNotes] = useState("");
  const [notesLoading, setNotesLoading] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [localMeetLink, setLocalMeetLink] = useState("");
  const [meetLinkLoading, setMeetLinkLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);
  let [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const fetchPodSession = async () => {
      const id = searchParams.get("id");
      const podSession = await API.graphql({
        query: GetPodSession,
        variables: { id: id },
      });
      console.log(podSession);
      fetchOrganization(podSession.data.getPodSession.organizationId);
      setPodSession(podSession.data.getPodSession);
      setLocalNotes(podSession.data.getPodSession.notes || "");
      setLocalMeetLink(podSession.data.getPodSession.googleMeetLink || "");
      setLoading(false);
    };

    const fetchOrganization = async (id) => {
      const organization = await API.graphql({
        query: GetOrganizationDetails,
        variables: { organizationId: id },
      });
      console.log(organization);
      console.log(JSON.parse(organization.data.getOrganizationDetails).body);
      setOrganization(
        JSON.parse(organization.data.getOrganizationDetails).body
      );
    };

    fetchPodSession();
  }, [searchParams]);

  useEffect(() => {
    if (podSession && organization) {
      const originalTitle = document.title;
      document.title = `${podSession.testerName} | ${organization.name}`;

      return () => {
        document.title = originalTitle;
      };
    }
  }, [podSession, organization]);

  useEffect(() => {
    const fetchUser = async () => {
      Auth.currentUserInfo()
        .then(async (user) => {
          console.log(user);
          setUserEmail(user.attributes.email);
          const userData = await API.graphql({
            query: getUser,
            variables: { id: user.id },
          });
          console.log("USA", userData);

          if (userData.data.getUser === null) {
            const newUser = await API.graphql({
              query: createUser,
              variables: {
                input: {
                  id: user.id,
                  email: user.attributes.email,
                },
              },
            });
            console.log("New User", newUser);
            setUser(newUser.data.createUser);
          } else {
            setUser(userData.data.getUser);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    };
    fetchUser();
  }, []);

  const handleImageClick = (imageUrl, imageType) => {
    setSelectedImage({ url: imageUrl, type: imageType });
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const handleGenerateGoogleMeetLink = async () => {
    if (!isUserTokens) {
      console.log("User does not have tokens");
      setShowLoginModal(true);
      return;
    } else {
      console.log("User has tokens");
      setMeetLoading(true);
      const data = await API.graphql({
        query: getGoogleRoomDetails,
        variables: {
          action: "createRoom",
          payload: JSON.stringify({
            clientId: podSession.clientId,
            accessToken: user.googleAccessToken,
            refreshToken: user.googleRefreshToken,
          }),
        },
      });
      console.log(data);
      let parsed = JSON.parse(data.data.getGoogleRoomDetails);
      console.log(parsed);
      if (parsed.statusCode === 200) {
        console.log("success");
        handleUpdateSessionOnMeetLink(parsed.body[0].meetingUri);
      }
    }
  };

  const isUserTokens =
    user?.googleAccessToken &&
    user?.googleRefreshToken &&
    user?.googleTokenExpiry;

  const updateUserTokens = async (accessToken, refreshToken, expiry) => {
    const userData = await API.graphql({
      query: updateUser,
      variables: {
        input: {
          id: user.id,
          googleAccessToken: accessToken,
          googleRefreshToken: refreshToken,
          googleTokenExpiry: expiry,
        },
      },
    });

    setUser(userData.data.getUser);
    // refresh the page
    window.location.reload();
    setShowLoginModal(false);
  };

  const getUserTokens = async (code) => {
    const data = await API.graphql({
      query: getGoogleRoomDetails,
      variables: {
        action: "getInitialTokens",
        payload: JSON.stringify({
          code: code,
        }),
      },
    });

    let parsed = JSON.parse(data.data.getGoogleRoomDetails);
    if (parsed.statusCode === 200) {
      updateUserTokens(
        parsed.body.access_token,
        parsed.body.refresh_token,
        parsed.body.expiry_date
      );
    }
  };

  const handleLoginWithGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      getUserTokens(codeResponse);
    },
    flow: "auth-code",
    redirect_uri: "postmessage",
    scope:
      "https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/meetings.space.readonly https://www.googleapis.com/auth/meetings.space.created https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/drive.meet.readonly https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive",
  });

  const handleUpdateSessionOnMeetLink = async (meetLink) => {
    console.log(meetLink);
    const data = await API.graphql({
      query: UpdatePodSession,
      variables: {
        input: {
          id: podSession.id,
          googleMeetLink: meetLink,
        },
      },
      authMode: "AMAZON_COGNITO_USER_POOLS",
    });
    setPodSession((prev) => ({
      ...prev,
      googleMeetLink: meetLink,
    }));
    setLocalMeetLink(meetLink);
    setMeetLoading(false);

    console.log(data);
  };

  const handleSaveNotes = async () => {
    setNotesLoading(true);
    const data = await API.graphql({
      query: UpdatePodSession,
      variables: {
        input: { id: podSession.id, notes: localNotes },
      },
    });
    setPodSession((prev) => ({
      ...prev,
      notes: localNotes,
    }));
    setNotesLoading(false);
  };

  const handleSaveMeetLink = async () => {
    setMeetLinkLoading(true);
    const data = await API.graphql({
      query: UpdatePodSession,
      variables: {
        input: { id: podSession.id, googleMeetLink: localMeetLink },
      },
    });
    setPodSession((prev) => ({
      ...prev,
      googleMeetLink: localMeetLink,
    }));
    setMeetLinkLoading(false);
  };

  const handleVerifyIdentity = async () => {
    const data = await API.graphql({
      query: UpdatePodSession,
      variables: {
        input: { id: podSession.id, verified_by: userEmail },
      },
    });
    setPodSession((prev) => ({
      ...prev,
      verified_by: userEmail,
    }));
  };

  const generateTableData = () => {
    if (!podSession) return [];

    const sessionLink = `https://www.monitoredu.live/pod-session?id=${podSession.id}`;
    const date = new Date().toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    // data string should have all key value pairs from podSession and podSession.fields
    // and should be in the format of key: value
    const dataString = Object.entries(podSession)
      .filter(
        ([key]) =>
          key !== "id" &&
          key !== "createdAt" &&
          key !== "idPictureUrlPath" &&
          key !== "updatedAt" &&
          key !== "selfiePictureUrlPath" &&
          key !== "iconfirm" &&
          key !== "timer" &&
          key !== "video" &&
          key !== "type" &&
          key !== "__typename" &&
          key !== "video" &&
          key !== "typeA" &&
          key !== "iConfirm" &&
          key !== "googleMeetLink" &&
          key !== "verified_by" &&
          key !== "notes"
      )
      .map(([key, value]) => {
        if (key === "fields") {
          return value.map((item) => `${item.key}: ${item.value}`).join(",\n");
        }
        return `${key}: ${value}`;
      })
      .join(",\n");

    return [
      {
        sessionId: podSession.id,
        sessionLink: sessionLink,
        date: date,
        proctor: "",
        client: podSession.organizationName || "",
        flag: "",
        student: podSession.testerName || "",
        receiptNumber:
          podSession.fields.find((field) => field.key === "receipt")?.value ||
          "",
        didCheckId: "",
        didCheckRoom: "",
        didCheckWristsEars: "",
        email: podSession.testerEmail || "",
        phone: podSession.testerPhone || "",
        instructorLastName:
          podSession.fields.find((field) => field.key === "instructor")
            ?.value || "",
        examName:
          podSession.fields.find((field) => field.key === "exam")?.value || "",
        data: dataString,
        startTime: "",
        endTime: "",
        didConfirmSubmission: "",
        primaryProctorNotes: "",
        primaryProctorLink: "",
        secondaryProctorLink: "",
        secondaryProctorName: "",
        sessionId2: podSession.id,
        sessionLink2: sessionLink,
      },
    ];
  };

  const tableHeaders = [
    "Session ID",
    "Session Link",
    "Date",
    "Proctor",
    "Client",
    "Flag",
    "Student",
    "Receipt #",
    "Did you check ID",
    "Did you check the room",
    "Did you check wrists, ears etc",
    "Email",
    "Phone",
    "Instructor Last Name",
    "Exam Name",
    "Data",
    "Start Time",
    "End Time",
    "Did you confirm submission",
    "Primary Proctor Notes (materials used, issues)",
    "Primary Proctor link",
    "Secondary Proctor Link",
    "Secondary Proctor Name",
    "session-id",
    "session-link",
  ];

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!loading && !podSession) {
    return <div>Pod session not found</div>;
  }

  return (
    <Box>
      <Box
        align="start"
        justify="center"
        fill
        direction="row"
        gap="medium"
        pad={{ horizontal: "medium", vertical: "small" }}
        height={{ min: "medium" }}
      >
        {/* Tester Info */}
        <Card fill height={{ min: "large" }}>
          <CardHeader
            pad="medium"
            border={{ side: "bottom", color: "gray", size: "xsmall" }}
          >
            <Text weight="bold" size="large">
              {podSession.testerName}
            </Text>
            <Text weight="bold" size="large">
              {podSession.organizationName}
            </Text>
          </CardHeader>
          <CardBody pad="medium">
            <Box direction="column" gap="small">
              <NameValueList>
                <NameValuePair name="Email">
                  <Text>{podSession.testerEmail}</Text>
                </NameValuePair>
                <NameValuePair name="Phone">
                  <Text>{podSession.testerPhone}</Text>
                </NameValuePair>
                {podSession.fields.map((field) => (
                  <NameValuePair name={field.key} key={field.key}>
                    <Text>{field.value}</Text>
                  </NameValuePair>
                ))}
                {podSession.verified_by && (
                  <NameValuePair name="ID Verified By">
                    <Text>{podSession.verified_by}</Text>
                  </NameValuePair>
                )}
                <NameValuePair name="Google Meet Link">
                  <Box direction="row" gap="small" align="center">
                    <TextInput
                      value={localMeetLink}
                      onChange={(e) => setLocalMeetLink(e.target.value)}
                      placeholder="Enter Google Meet link"
                      size="small"
                    />
                    <Button
                      label="Save"
                      size="small"
                      primary
                      onClick={handleSaveMeetLink}
                      disabled={meetLinkLoading}
                    />
                    {meetLinkLoading && <Text size="small">Saving...</Text>}
                  </Box>
                </NameValuePair>
              </NameValueList>
            </Box>
            <Box
              direction="row"
              align="center"
              justify="center"
              gap="small"
              pad="small"
            >
              <Box>
                <Image
                  src={
                    "https://monitoredu.jotform.com/" +
                    podSession.idPictureUrlPath
                  }
                  fit="contain"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    handleImageClick(
                      "https://monitoredu.jotform.com/" +
                        podSession.idPictureUrlPath,
                      "ID Picture"
                    )
                  }
                />
                <Text>ID Picture (Click to enlarge)</Text>
              </Box>
              <Box>
                <Image
                  src={
                    "https://monitoredu.jotform.com/" +
                    podSession.selfiePictureUrlPath
                  }
                  fit="contain"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    handleImageClick(
                      "https://monitoredu.jotform.com/" +
                        podSession.selfiePictureUrlPath,
                      "Selfie Picture"
                    )
                  }
                />
                <Text>Selfie Picture (Click to enlarge)</Text>
              </Box>
            </Box>
            <Box gap="small">
              {(!podSession.verified_by || podSession.verified_by === "") && (
                <Button
                  label="I have verified the ID and selfie"
                  primary
                  color="status-ok"
                  onClick={handleVerifyIdentity}
                />
              )}
              {meetLoading && <Text>Loading...</Text>}
              {/* Dynamic action button based on organization configuration */}
              {!meetLoading &&
                (() => {
                  const action = getOrganizationAction(
                    podSession.organizationId
                  );
                  if (!action.showCondition(podSession)) return null;

                  return (
                    <Button
                      label={action.label}
                      primary
                      color="black"
                      onClick={() => {
                        if (action.action === "redirect") {
                          window.open(action.url, "_blank");
                        } else if (action.action === "generate") {
                          handleGenerateGoogleMeetLink();
                        }
                      }}
                      disabled={meetLoading}
                    />
                  );
                })()}
            </Box>
          </CardBody>
        </Card>
        {/* Alerts */}
        <Card fill height={{ min: "large" }}>
          <CardHeader
            align="center"
            justify="center"
            pad="medium"
            border={{ side: "bottom", color: "gray", size: "xsmall" }}
          >
            <Box direction="row" justify="center" align="center">
              <Text weight="bold" size="large">
                Alerts
              </Text>
            </Box>
          </CardHeader>
          <CardBody>
            <Box direction="row" justify="start" align="start" pad="medium">
              <Text style={{ whiteSpace: "pre-wrap" }}>
                {organization?.agent_alerts || "No alerts"}
              </Text>
            </Box>
          </CardBody>
        </Card>

        {/* Image Modal */}
        {selectedImage && (
          <Layer
            full
            modal
            onClickOutside={closeModal}
            onEsc={closeModal}
            background="rgba(0, 0, 0, 0.8)"
          >
            <Box
              fill
              align="center"
              justify="center"
              pad="large"
              onClick={closeModal}
            >
              <Box
                background="white"
                pad="medium"
                round="small"
                onClick={(e) => e.stopPropagation()}
              >
                <Box
                  direction="row"
                  justify="between"
                  align="center"
                  margin={{ bottom: "small" }}
                >
                  <Text weight="bold" size="large">
                    {selectedImage.type}
                  </Text>
                  <Button
                    icon={<span style={{ fontSize: "24px" }}>×</span>}
                    onClick={closeModal}
                    plain
                    hoverIndicator
                  />
                </Box>
                <Image
                  src={selectedImage.url}
                  fit="contain"
                  style={{ maxHeight: "80vh", maxWidth: "80vw" }}
                />
              </Box>
            </Box>
          </Layer>
        )}
        {/* Login Modal */}
        {showLoginModal && (
          <Layer position="right" full="vertical" modal>
            <Box width="medium" pad="medium">
              <Text>
                Login With Google, Click below to authorize google meets
              </Text>
              <Button
                label="Login With Google"
                primary
                color="black"
                onClick={() => handleLoginWithGoogle()}
              />
            </Box>
          </Layer>
        )}
      </Box>
      {/* Notes */}
      <Box align="center" pad="medium" height="medium" gap="small">
        <Text>
          Notes: Use this space to take notes about the session if the sheets
          are not working or slow
        </Text>
        <TextArea
          value={localNotes}
          onChange={(e) => setLocalNotes(e.target.value)}
          placeholder="Enter your notes here"
          fill
        />
        <Button
          label="Save Notes"
          primary
          color="black"
          onClick={() => handleSaveNotes()}
        />
        {notesLoading && <Text>Saving...</Text>}
      </Box>

      {/* Table Section */}
      <Box align="center" pad="medium" gap="small">
        Use the button below to show the full row that you can copy and paste
        into the sheet
        <Button
          label={
            showTable ? "Hide Session Data Table" : "Show Session Data Table"
          }
          primary
          color="black"
          onClick={() => setShowTable(!showTable)}
        />
        {showTable && (
          <Box overflow="auto" width="100%">
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                border: "1px solid #ddd",
                fontSize: "14px",
                fontFamily: "Arial, sans-serif",
              }}
            >
              <thead>
                <tr>
                  {tableHeaders.map((header, index) => (
                    <th
                      key={index}
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "left",
                        backgroundColor: "#f2f2f2",
                        fontWeight: "bold",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {generateTableData().map((row, index) => (
                  <tr key={index}>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.sessionId}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.sessionLink}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.date}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.proctor}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.client}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.flag}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.student}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.receiptNumber}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.didCheckId}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.didCheckRoom}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.didCheckWristsEars}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.email}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.phone}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.instructorLastName}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.examName}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.data}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.startTime}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.endTime}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.didConfirmSubmission}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.primaryProctorNotes}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.primaryProctorLink}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.secondaryProctorLink}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.secondaryProctorName}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.sessionId2}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {row.sessionLink2}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>
      <MonitorEDUQuickButtons />
    </Box>
  );
}
